import type { NextPageWithLayout } from 'next'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import { Flex } from 'components/Layout'
import { Loader, LOADER_SIZE } from 'components/Loader'
import { ROUTE } from 'constants/routes'
import { useUserAuth } from 'providers/userAuth'

interface WithRedirectProps {
  redirectRouteForAll?: boolean | string
  redirectRouteForClient?: string
  redirectRouteForExpert?: string
  redirectRouteForAdmin?: string
  isWithReturnUrl?: boolean
}

export const withRedirect =
  ({
    redirectRouteForAll,
    redirectRouteForClient = ROUTE.CLIENT(),
    redirectRouteForExpert = ROUTE.EXPERT_DASHBOARD(),
    redirectRouteForAdmin = ROUTE.ADMIN(),
    isWithReturnUrl,
  }: WithRedirectProps = {}) =>
  <CP extends object>(Component: NextPageWithLayout<CP>) => {
    const WrappedComponent = (props) => {
      const {
        user,
        isPageLoading,
        isClientSignedIn,
        isExpertSignedIn,
        isAdminSignedIn,
        isMFAEnrollRequired,
      } = useUserAuth()
      const router = useRouter()
      const {
        query: { returnUrl },
      } = router
      useEffect(() => {
        if (user && !isMFAEnrollRequired) {
          if (
            isWithReturnUrl &&
            returnUrl &&
            // I'm not able to handle where does the sign-up action come from so we need to filter redirect urls
            // TODO: refactor to Enum or Array
            !returnUrl.includes('sign-up') &&
            !returnUrl.includes('sign-in') &&
            ((isClientSignedIn &&
              // TODO: get all protected routes and prevent redirect loop
              !returnUrl.includes('/admin') &&
              !returnUrl.includes('/expert')) ||
              (isExpertSignedIn &&
                !returnUrl.includes('/admin') &&
                // TODO: get all protected routes and prevent redirect loop
                !returnUrl.includes('/client')) ||
              (isAdminSignedIn &&
                // TODO: get all protected routes and prevent redirect loop
                !returnUrl.includes('/expert') &&
                !returnUrl.includes('/client') &&
                !returnUrl.includes('checkout')))
          ) {
            router.push(decodeURIComponent(returnUrl as string))
          } else if (typeof redirectRouteForAll === 'string') {
            router.replace(redirectRouteForAll)
          } else if (isClientSignedIn) {
            router.replace(redirectRouteForClient)
          } else if (isExpertSignedIn) {
            router.replace(redirectRouteForExpert)
          } else if (isAdminSignedIn) {
            router.replace(redirectRouteForAdmin)
          }
        }
      }, [
        router,
        user,
        returnUrl,
        isClientSignedIn,
        isExpertSignedIn,
        isAdminSignedIn,
        isMFAEnrollRequired,
      ])
      if ((isPageLoading || user) && !isMFAEnrollRequired) {
        return (
          <Flex alignItems="center" justifyContent="center" height="100vh">
            <Loader
              data-test-id={DATA_TEST_ID.PAGE_LOADER}
              size={LOADER_SIZE.XXL}
            />
          </Flex>
        )
      }
      return <Component {...props} />
    }
    WrappedComponent.getLayout = Component.getLayout
    WrappedComponent.seoProps = Component.seoProps
    return WrappedComponent
  }
